import { useRef, useEffect, useState, useCallback } from 'react'
import * as rive from '@rive-app/canvas'
import { useInView } from 'framer-motion'

// Rive Fit enum for better type safety
export enum RiveFit {
  HEIGHT = rive.Fit.FitHeight,
  WIDTH = rive.Fit.FitWidth,
  FILL = rive.Fit.Fill,
  CONTAIN = rive.Fit.Contain,
  COVER = rive.Fit.Cover,
  NONE = rive.Fit.None,
  /** Responsive artboards designed with Rive Layout constraints */
  LAYOUT = rive.Fit.Layout
}

// Rive Alignment enum
export enum RiveAlignment {
  TOP_LEFT = rive.Alignment.TopLeft,
  TOP_CENTER = rive.Alignment.TopCenter,
  TOP_RIGHT = rive.Alignment.TopRight,
  CENTER_LEFT = rive.Alignment.CenterLeft,
  CENTER = rive.Alignment.Center,
  CENTER_RIGHT = rive.Alignment.CenterRight,
  BOTTOM_LEFT = rive.Alignment.BottomLeft,
  BOTTOM_CENTER = rive.Alignment.BottomCenter,
  BOTTOM_RIGHT = rive.Alignment.BottomRight
}

// Component props interface
export interface RiveComponentProps {
  /** Path to the Rive animation file (.riv) */
  src: string

  /** Animation names to play - single string or array */
  animations?: string | string[]

  /** State machine names to activate - single string or array */
  stateMachines?: string | string[]

  /** Auto-start playback @default true */
  autoplay?: boolean

  /** How animation fits in container @default RiveFit.CONTAIN */
  fit?: RiveFit

  /** Animation alignment in container @default RiveAlignment.CENTER */
  alignment?: RiveAlignment

  /** CSS classes for canvas element */
  className?: string

  /** Inline styles for canvas element */
  style?: React.CSSProperties

  /** Called when artboard is ready, before `textRunValues` are applied */
  onArtboardReady?: (rive: rive.Rive) => void

  /** Called when Rive instance loads */
  onLoad?: (rive: rive.Rive) => void

  /** Called when animation starts playing */
  onPlay?: () => void

  /** Called when animation pauses */
  onPause?: () => void

  /** Called when animation stops */
  onStop?: () => void

  /** Called when animation loops */
  onLoop?: () => void

  /** Called when state machine changes state */
  onStateChange?: (stateMachineName: string, stateName: string) => void

  /** Called when a Rive event is received */
  onRiveEventReceived?: (event: rive.Event) => void

  /** Called when error occurs */
  onError?: (error: Error) => void

  /** Play only when in viewport @default false */
  playOnView?: boolean

  /** Visibility threshold for playOnView (0-1) @default 0.5 */
  viewThreshold?: number

  /** Text values for Rive text runs */
  textRunValues?: Record<string, string>

  /** Specific artboard to use */
  artboard?: string

  /**
   * Bind the default view model instance to the artboard (enables View Model triggers/properties).
   * @default false
   */
  autoBind?: boolean

  /** Fallback component to render when Rive fails to load */
  fallback?: React.ReactNode
}

// Error boundary component
class RiveError extends Error {
  constructor(
    message: string,
    public originalError?: Error
  ) {
    super(message)
    this.name = 'RiveError'
  }
}

const RiveComponent: React.FC<RiveComponentProps> = ({
  src,
  animations,
  stateMachines,
  autoplay = true,
  fit = RiveFit.CONTAIN,
  alignment = RiveAlignment.CENTER,
  className = '',
  style = {},
  onArtboardReady,
  onLoad,
  onPlay,
  onPause,
  onStop,
  onLoop,
  onStateChange,
  onRiveEventReceived,
  onError,
  playOnView = false,
  viewThreshold = 0.5,
  textRunValues = {},
  artboard,
  autoBind = false,
  fallback
}) => {
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const riveInstanceRef = useRef<rive.Rive | null>(null)
  const isInitializedRef = useRef(false)
  const previousStateMachineRef = useRef<string | string[] | undefined>(undefined)
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // State
  const [isLoaded, setIsLoaded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Intersection observer for play on view
  const isInView = useInView(canvasRef, {
    once: false,
    amount: viewThreshold
  })

  // Clear timeout helper
  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current)
      loadTimeoutRef.current = null
    }
  }, [])

  // Unified error handler
  const handleError = useCallback(
    (message: string, originalError?: Error | unknown) => {
      clearLoadTimeout()
      const riveError = new RiveError(message, originalError instanceof Error ? originalError : undefined)
      setError(riveError)
      onError?.(riveError)
      console.error('RiveComponent Error:', riveError)
    },
    [onError, clearLoadTimeout]
  )

  // Initialize Rive instance
  const initializeRive = useCallback(async () => {
    if (!canvasRef.current || isInitializedRef.current) return

    try {
      // Clear any existing timeout
      clearLoadTimeout()

      // Cleanup existing instance
      if (riveInstanceRef.current) {
        try {
          riveInstanceRef.current.cleanup()
        } catch (error) {
          console.warn('Error during Rive cleanup:', error)
        }
        riveInstanceRef.current = null
      }
      setIsLoaded(false)
      setIsPlaying(false)
      isInitializedRef.current = false

      // Set a timeout to detect loading failures (e.g., wasm loading issues)
      loadTimeoutRef.current = setTimeout(() => {
        if (!isLoaded && !isInitializedRef.current) {
          handleError(
            'Rive failed to load within timeout period. This may be due to wasm loading issues or network problems.',
            new Error('Load timeout')
          )
        }
      }, 10000) // 10 second timeout

      // Create new Rive instance
      const riveInstance = new rive.Rive({
        src,
        canvas: canvasRef.current,
        artboard,
        animations: animations ? (Array.isArray(animations) ? animations : [animations]) : undefined,
        stateMachines: stateMachines ? (Array.isArray(stateMachines) ? stateMachines : [stateMachines]) : undefined,
        autoplay: autoplay && (!playOnView || isInView),
        autoBind,
        onLoad: () => {
          // Clear timeout on successful load
          clearLoadTimeout()

          setIsLoaded(true)
          isInitializedRef.current = true

          onArtboardReady?.(riveInstance)

          // Set text run values if provided
          if (Object.keys(textRunValues).length > 0) {
            try {
              Object.entries(textRunValues).forEach(([key, value]) => {
                riveInstance?.setTextRunValue(key, value)
              })
            } catch (error) {
              console.error('Setting text run values failed:', error)
            }
          }

          // Set up layout
          riveInstance.layout = new rive.Layout({
            fit: fit as any,
            alignment: alignment as any
          })

          // Redraw after load
          if (riveInstance && canvasRef.current) {
            try {
              riveInstance.resizeDrawingSurfaceToCanvas()
            } catch (error) {
              console.error('Redraw failed:', error)
            }
          }

          onLoad?.(riveInstance)
          // Subscribe to Rive events if callback is provided
          if (onRiveEventReceived) {
            riveInstance.on(rive.EventType.RiveEvent, (event: rive.Event) => {
              onRiveEventReceived(event)
            })
          }
        },
        onLoadError: ((event: any) => {
          const errorMessage =
            event instanceof ErrorEvent
              ? event.message
              : event?.message || event?.type === 'error'
                ? 'Failed to load Rive animation'
                : 'Unknown error occurred'

          const originalError =
            event instanceof ErrorEvent && event.error
              ? event.error
              : event instanceof Error
                ? event
                : new Error(errorMessage)

          handleError(
            `Rive failed to load: ${errorMessage}. This may be due to wasm loading issues, network problems, or invalid file.`,
            originalError
          )
        }) as rive.EventCallback,
        onPlay: () => {
          setIsPlaying(true)
          onPlay?.()
        },
        onPause: () => {
          setIsPlaying(false)
          onPause?.()
        },
        onStop: () => {
          setIsPlaying(false)
          onStop?.()
        },
        onLoop: () => {
          onLoop?.()
        },
        onStateChange: (event) => {
          if (
            event.data &&
            typeof event.data === 'object' &&
            'stateMachineName' in event.data &&
            'stateName' in event.data
          ) {
            onStateChange?.(event.data.stateMachineName as string, event.data.stateName as string)
          }
        }
      })

      riveInstanceRef.current = riveInstance
      setError(null)
    } catch (error) {
      handleError('Rive initialization failed', error)
    }
  }, [
    src,
    artboard,
    autoBind,
    animations,
    stateMachines,
    autoplay,
    playOnView,
    isInView,
    fit,
    alignment,
    textRunValues,
    handleError,
    clearLoadTimeout,
    onArtboardReady,
    onRiveEventReceived,
    onStateChange,
    onLoad,
    onPlay,
    onPause,
    onStop,
    onLoop
  ])

  // When src changes, tear down so the next file loads. Without this, isInitializedRef
  // blocks re-init and onStop fires immediately (e.g. intro after stage-complete animation).
  useEffect(() => {
    isInitializedRef.current = false
    setIsLoaded(false)
    setIsPlaying(false)
    setError(null)
    if (riveInstanceRef.current) {
      try {
        riveInstanceRef.current.cleanup()
      } catch (error) {
        console.warn('Error during Rive cleanup on src change:', error)
      }
      riveInstanceRef.current = null
    }
  }, [src])

  // Effect for initialization
  useEffect(() => {
    initializeRive()
  }, [initializeRive])

  // Effect for play on view
  useEffect(() => {
    if (playOnView && isLoaded) {
      if (isInView) {
        if (riveInstanceRef.current && !isPlaying) {
          try {
            riveInstanceRef.current.play()
            setIsPlaying(true)
            onPlay?.()
          } catch (error) {
            console.error('Play failed:', error)
          }
        }
      } else {
        if (riveInstanceRef.current && isPlaying) {
          try {
            riveInstanceRef.current.pause()
            setIsPlaying(false)
            onPause?.()
          } catch (error) {
            console.error('Pause failed:', error)
          }
        }
      }
    }
  }, [playOnView, isLoaded, isInView, isPlaying])

  // Effect for text run values updates
  useEffect(() => {
    if (isLoaded && Object.keys(textRunValues).length > 0 && riveInstanceRef.current) {
      try {
        Object.entries(textRunValues).forEach(([key, value]) => {
          riveInstanceRef.current?.setTextRunValue(key, value)
        })
      } catch (error) {
        console.error('Setting text run values failed:', error)
      }
    }
  }, [isLoaded, textRunValues])

  // Effect for state machine changes
  useEffect(() => {
    // Only reinitialize if state machine actually changed and we have a loaded instance
    const hasStateMachineChanged = JSON.stringify(previousStateMachineRef.current) !== JSON.stringify(stateMachines)

    if (isLoaded && riveInstanceRef.current && stateMachines && hasStateMachineChanged) {
      try {
        // Update the previous state machine reference
        previousStateMachineRef.current = stateMachines

        // Clean up current instance
        if (riveInstanceRef.current) {
          riveInstanceRef.current.cleanup()
          riveInstanceRef.current = null
        }

        // Reset state flags
        setIsLoaded(false)
        setIsPlaying(false)
        isInitializedRef.current = false

        // Small delay to ensure cleanup is complete before reinitializing
        setTimeout(() => {
          initializeRive()
        }, 10)
      } catch (error) {
        console.error('State machine change failed:', error)
      }
    } else if (!previousStateMachineRef.current && stateMachines) {
      // Set initial state machine reference
      previousStateMachineRef.current = stateMachines
    }
  }, [stateMachines, isLoaded])

  // Effect for window resize
  useEffect(() => {
    const handleResize = () => {
      if (riveInstanceRef.current && canvasRef.current) {
        try {
          riveInstanceRef.current.resizeDrawingSurfaceToCanvas()
        } catch (error) {
          console.error('Redraw failed:', error)
        }
      }
    }

    window.addEventListener('resize', handleResize, { passive: true })
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearLoadTimeout()

      if (riveInstanceRef.current) {
        try {
          riveInstanceRef.current.cleanup()
        } catch (error) {
          console.warn('Error during Rive cleanup:', error)
        }
        riveInstanceRef.current = null
      }
    }
  }, [clearLoadTimeout])

  // Error display - render fallback if provided, otherwise return null
  if (error) {
    return fallback ? <>{fallback}</> : null
  }

  return (
    <canvas
      ref={canvasRef}
      className={`rive-canvas ${className}`}
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        ...style
      }}
    />
  )
}

export default RiveComponent
